import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { Link, useLocation, Outlet } from "react-router-dom"
import { getAuth, onAuthStateChanged, signOut, signInWithPopup, GoogleAuthProvider } from "firebase/auth"
import {
  AppstoreOutlined,
  DashboardOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ControlOutlined,
  ClockCircleOutlined,
  StopOutlined,
  TeamOutlined,
  BookOutlined,
  AuditOutlined,
  ScheduleOutlined,
  SafetyCertificateOutlined,
  RiseOutlined,
  EuroOutlined,
  RobotOutlined,
  HeartOutlined,
  HistoryOutlined,
} from "@ant-design/icons"
import { useAuth } from "../authContext"
import { canAccessPage, OPTIMIZATION_TAB_KEYS } from "../pageAccess"
import { prefetchPageChunk } from "../lazyPages"
import { getDataHealthFunc } from "../App"
import FeedbackButton from "./elements/FeedbackButton"

/** Monitoring.tsx dispatches this with `{ failingKeys }` after a refresh or "Mark as seen". */
export const MONITORING_REFRESHED_EVENT = "viridian:monitoring-refreshed"
const MONITORING_POLL_MS = 15 * 60 * 1000
const MONITORING_ACK_STORAGE_KEY = "monitoring-acked-keys-v1"

/** Failing keys this viewer marked as seen (per browser). */
export const loadAckedMonitoringKeys = (): string[] => {
  try {
    const raw = window.localStorage.getItem(MONITORING_ACK_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : []
  } catch {
    return []
  }
}

/** Hide the badge for these failures; a check that recovers and fails again shows up again. */
export const ackMonitoringKeys = (failingKeys: string[]) => {
  try {
    window.localStorage.setItem(MONITORING_ACK_STORAGE_KEY, JSON.stringify(failingKeys))
  } catch {
    // storage unavailable — badge just stays
  }
  window.dispatchEvent(new CustomEvent(MONITORING_REFRESHED_EVENT, { detail: { failingKeys } }))
}

/** Unseen failing checks + jobs for the Monitoring nav badge; polls while the app is open. */
const useMonitoringUnseen = (enabled: boolean) => {
  const [ unseen, setUnseen ] = useState(0)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const apply = (failingKeys: string[]) => {
      // Forget acks for checks that recovered, so a relapse counts again.
      const acked = loadAckedMonitoringKeys().filter(k => failingKeys.includes(k))
      try { window.localStorage.setItem(MONITORING_ACK_STORAGE_KEY, JSON.stringify(acked)) } catch { /* ignore */ }
      if (!cancelled) setUnseen(failingKeys.filter(k => !acked.includes(k)).length)
    }
    const load = async () => {
      try {
        const res = await getDataHealthFunc()
        const data = res.data as { checks?: { key: string; status: string }[]; jobs?: { key: string; status: string }[] }
        apply([ ...(data.checks || []), ...(data.jobs || []) ].filter(r => r.status !== "ok").map(r => r.key))
      } catch {
        // badge is best-effort
      }
    }
    const onRefreshed = (e: Event) => apply((e as CustomEvent<{ failingKeys?: string[] }>).detail?.failingKeys ?? [])
    load()
    const timer = window.setInterval(load, MONITORING_POLL_MS)
    window.addEventListener(MONITORING_REFRESHED_EVENT, onRefreshed)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      window.removeEventListener(MONITORING_REFRESHED_EVENT, onRefreshed)
    }
  }, [ enabled ])
  return unseen
}

type NavItem = {
  key: string
  label: string
  path: string
  icon: ReactNode
  /** Permission page key when different from `key` */
  accessKey?: string
  /** Any of these page keys grants access (used by Optimization hub). */
  accessKeys?: string[]
}
type NavSection = { section: string; items: NavItem[] }

const NAV: NavSection[] = [
  {
    section: "Assistant",
    items: [
      { key: "assistant", label: "Assistant", path: "/assistant", icon: <RobotOutlined className="nav-icon" /> },
    ],
  },
  {
    section: "Amazon",
    items: [
      { key: "amz-products", label: "AMZ Products", path: "/amz-products", icon: <ShopOutlined className="nav-icon" /> },
      { key: "product-planning", label: "Product Planning", path: "/product-planning", icon: <ScheduleOutlined className="nav-icon" /> },
      { key: "listing-audit", label: "Listing Health", path: "/listing-audit", icon: <AuditOutlined className="nav-icon" /> },
      { key: "account-health", label: "Account Health", path: "/account-health", icon: <SafetyCertificateOutlined className="nav-icon" /> },
      { key: "repricer", label: "Repricer", path: "/repricer", icon: <EuroOutlined className="nav-icon" /> },
    ],
  },
  {
    section: "PPC",
    items: [
      { key: "dashboard", label: "Dashboard", path: "/dashboard", icon: <DashboardOutlined className="nav-icon" /> },
      { key: "optimization", label: "Optimization", path: "/optimization", icon: <ControlOutlined className="nav-icon" />, accessKeys: [ ...OPTIMIZATION_TAB_KEYS ] },
      { key: "dayparting", label: "Dayparting", path: "/dayparting", icon: <ClockCircleOutlined className="nav-icon" /> },
      { key: "negatives", label: "Negatives", path: "/negatives", icon: <StopOutlined className="nav-icon" /> },
      { key: "harvesting", label: "Harvesting", path: "/harvesting", icon: <RiseOutlined className="nav-icon" /> },
    ],
  },
  {
    section: "Kaufland",
    items: [
      { key: "orders", label: "Orders", path: "/orders", icon: <ShoppingCartOutlined className="nav-icon" /> },
      { key: "products", label: "KFL Products", path: "/products", icon: <AppstoreOutlined className="nav-icon" /> },
    ],
  },
  {
    section: "System",
    items: [
      { key: "users", label: "Users", path: "/users", icon: <TeamOutlined className="nav-icon" /> },
      { key: "history", label: "Change history", path: "/history", icon: <HistoryOutlined className="nav-icon" /> },
      { key: "monitoring", label: "Monitoring", path: "/monitoring", icon: <HeartOutlined className="nav-icon" />, accessKey: "settings" },
      { key: "settings", label: "Settings", path: "/settings", icon: <SettingOutlined className="nav-icon" /> },
    ],
  },
]

const PAGE_TITLES: Record<string, string> = {
  assistant: "Viridian Assistant",
  orders: "Kaufland Orders",
  products: "Kaufland Products",
  "amz-products": "AMZ Products",
  "product-planning": "Product Planning",
  "listing-audit": "Listing Health",
  "account-health": "Account Health",
  dashboard: "Dashboard",
  optimization: "Optimization",
  portfolios: "Portfolios",
  modes: "Modes",
  events: "Events",
  keywords: "Keywords",
  marketplaces: "Marketplaces",
  runs: "Optimizer Runs",
  dayparting: "Dayparting",
  negatives: "Adding Negatives",
  harvesting: "Keyword Harvesting",
  repricer: "Repricer",
  settings: "Settings",
  monitoring: "Monitoring",
  history: "Change history",
  users: "Users",
  forbidden: "Access Denied",
}

const PAGE_LOGIC_DOCS: Partial<Record<string, string>> = {
  optimization: "https://hackmd.io/@jIydoHrSQvia6HYCZ2z95Q/S1EiLZi1Gx",
  negatives: "https://hackmd.io/@jIydoHrSQvia6HYCZ2z95Q/Bk9S-28ZMl",
}

type PageHeaderExtrasContextValue = {
  setExtras: (extras: ReactNode) => void
  setTitleExtras: (extras: ReactNode) => void
}

const PageHeaderExtrasContext = createContext<PageHeaderExtrasContextValue | null>(null)

/** Render page-specific controls in the top bar (right of the page title). */
export const usePageHeaderExtras = (extras: ReactNode) => {
  const ctx = useContext(PageHeaderExtrasContext)
  useEffect(() => {
    if (!ctx) return
    ctx.setExtras(extras)
    return () => ctx.setExtras(null)
  }, [ ctx, extras ])
}

/** Render a suffix immediately after the page title (e.g. saving spinner). */
export const usePageHeaderTitleExtras = (extras: ReactNode) => {
  const ctx = useContext(PageHeaderExtrasContext)
  useEffect(() => {
    if (!ctx) return
    ctx.setTitleExtras(extras)
    return () => ctx.setTitleExtras(null)
  }, [ ctx, extras ])
}

/** First name or email local-part for "Hi, …" greeting (common app pattern). */
const greetingName = (displayName?: string | null, emailAddr?: string | null) => {
  const name = displayName?.trim()
  if (name) return name.split(/\s+/)[0]
  const addr = emailAddr?.trim()
  if (addr) {
    const local = addr.split("@")[0] || addr
    const segment = local.split(/[._-]/)[0] || local
    return segment.charAt(0).toUpperCase() + segment.slice(1)
  }
  return null
}

const BaseLayout = () => {
  const location = useLocation()
  const currentAddress = location.pathname.split("/")[1] || ""
  const [ currentPage, setCurrentPage ] = useState(currentAddress)
  const [ headerExtras, setHeaderExtras ] = useState<ReactNode>(null)
  const [ headerTitleExtras, setHeaderTitleExtras ] = useState<ReactNode>(null)
  const [ firebaseEmail, setFirebaseEmail ] = useState<string | null>(() => getAuth().currentUser?.email ?? null)
  const { allowedPages, email, displayName } = useAuth()
  const skipAuthInDev = process.env.NODE_ENV === "development"

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), user => setFirebaseEmail(user?.email ?? null))
    return unsub
  }, [])

  useEffect(() => { setCurrentPage(currentAddress) }, [ currentAddress ])

  const userEmail = email || firebaseEmail || null
  const shortName = greetingName(displayName, userEmail) || (skipAuthInDev ? "Dev" : "there")
  const showLogout = Boolean(firebaseEmail)
  const userTitle = [ displayName, userEmail ].filter(Boolean).join(" · ")

  const handleLogout = async () => {
    try {
      await signOut(getAuth())
    } catch (e) {
      console.error("Logout error:", e)
    }
  }

  const handleSignIn = async () => {
    try {
      await signInWithPopup(getAuth(), new GoogleAuthProvider())
    } catch (e) {
      console.error("Sign in error:", e)
    }
  }

  const visibleNav = useMemo(() => NAV.map(section => ({
    ...section,
    items: section.items.filter(item => {
      const keys = item.accessKeys ?? [ item.accessKey ?? item.key ]
      return keys.some(k => canAccessPage(allowedPages, k))
    }),
  })).filter(section => section.items.length > 0), [ allowedPages ])

  const monitoringFailing = useMonitoringUnseen(canAccessPage(allowedPages, "settings"))

  const firstNavPath = visibleNav[0]?.items[0]?.path ?? "/forbidden"
  const pageTitle = PAGE_TITLES[currentPage] ?? "Viridian"
  const logicDocUrl = PAGE_LOGIC_DOCS[currentPage]

  return (
    <PageHeaderExtrasContext.Provider value={{ setExtras: setHeaderExtras, setTitleExtras: setHeaderTitleExtras }}>
    <div className="app-layout">
      <aside className="side-nav">
        <Link to={firstNavPath} className="brand">
          <img src={`${process.env.PUBLIC_URL}/logo_viridian.png`} alt="" className="brand-mark" aria-hidden />
          Viridian
        </Link>
        {shortName ? (
          <div className="nav-user" title={userTitle || undefined}>
            <span className="nav-user-greeting">
              Hi, <span className="nav-user-name">{shortName}</span>
            </span>
            {showLogout ? (
              <>
                <span className="nav-user-sep" aria-hidden>·</span>
                <button type="button" className="nav-user-logout" onClick={handleLogout}>
                  Sign out
                </button>
              </>
            ) : skipAuthInDev ? (
              <>
                <span className="nav-user-sep" aria-hidden>·</span>
                <button type="button" className="nav-user-logout" onClick={handleSignIn}>
                  Sign in
                </button>
              </>
            ) : null}
          </div>
        ) : null}
        <div className="side-nav-links">
          {visibleNav.map(({ section, items }) => (
            <div key={section}>
              <div className="nav-section">{section}</div>
              {items.map(item => (
                <Link
                  key={item.key}
                  to={item.path}
                  className={currentPage === item.key ? "active" : undefined}
                  onMouseEnter={() => prefetchPageChunk(item.path)}
                  onFocus={() => prefetchPageChunk(item.path)}
                >
                  {item.icon}
                  {item.label}
                  {item.key === "monitoring" && monitoringFailing > 0 ? (
                    <span className="nav-badge" title={`${monitoringFailing} new problem(s) — open Monitoring, then "Mark as seen" to hide`}>{monitoringFailing}</span>
                  ) : null}
                </Link>
              ))}
            </div>
          ))}
        </div>
        <div className="side-nav-footer">
          <FeedbackButton pagePath={location.pathname + location.search} />
        </div>
      </aside>
      <div className="app-main">
        <header className="top-bar">
          <h2>
            {pageTitle}
            {headerTitleExtras}
            {logicDocUrl ? (
              <a
                href={logicDocUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="top-bar-logic-doc"
                title="Logic documentation (opens in new tab)"
              >
                <BookOutlined aria-hidden />
                Logic doc
              </a>
            ) : null}
          </h2>
          {headerExtras ? <div className="top-bar-extras">{headerExtras}</div> : null}
        </header>
        <main className="content">
          <div className="page-inner">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
    </PageHeaderExtrasContext.Provider>
  )
}

export default BaseLayout
